import { Medialink } from '../../../../api/medialink';
import { SlideSet } from '../../../../api/slide-set';
import { NodeWithoutDescription } from '../../../../api/table-of-content';
import { UserMaterial } from '../../../../api/user-material';
import { groupMaterialsByNode } from '../../../../utils/medialink';
import { flattenNodes } from '../../../../utils/tocNode';
import { SharedMaterial } from '../context/QueryMaterialContext';

const groupUserMaterialByNode = (userMaterials: UserMaterial[]) => {
  return userMaterials.reduce<Record<string, UserMaterial[]>>((groupedUserMaterials, userMaterial) => {
    return {
      ...groupedUserMaterials,
      [userMaterial.node]: [...(groupedUserMaterials[userMaterial.node] || []), userMaterial],
    };
  }, {});
};

const slideSetByNode = (slideSets: SlideSet[]) => {
  return slideSets.reduce<Record<string, SlideSet[]>>((groupedSlideSets, slideSet) => {
    return {
      ...groupedSlideSets,
      [slideSet.nodeId]: [...(groupedSlideSets[slideSet.nodeId] || []), slideSet],
    };
  }, {});
};

const groupSharedMaterialByNode = (sharedMaterial: SharedMaterial[]) => {
  return sharedMaterial.reduce<Record<string, SharedMaterial[]>>((groupedSharedMaterial, material) => {
    const nodeId = 'nodeId' in material ? material.nodeId : material.node;
    return {
      ...groupedSharedMaterial,
      [nodeId]: [...(groupedSharedMaterial[nodeId] || []), material],
    };
  }, {});
};

const getFallbackNodesById = (
  materials: Array<{
    hierarchy?: {
      id: string;
      title: string;
      prefix?: string;
    }[];
  }>,
) => {
  return materials.reduce<Record<string, NodeWithoutDescription>>((acc, material) => {
    const node = material.hierarchy?.[0];
    if (!node || acc[node.id]) return acc;

    return {
      ...acc,
      [node.id]: {
        id: node.id,
        title: node.title,
        displayPrefix: false,
        prefix: node.prefix,
      },
    };
  }, {});
};

export function groupResultsByModule(
  moduleIds: string[],
  medialinks: Medialink[] = [],
  userMaterial: UserMaterial[] = [],
  slideSets: SlideSet[] = [],
  sharedMaterial: SharedMaterial[] = [],
  nodesByModule: Record<string, NodeWithoutDescription[]>,
): {
  moduleId: string;
  count: number;
  nodes: NodeWithoutDescription[];
  groupedByNode: {
    [nodeId: string]: {
      medialinks: Medialink[];
      userMaterial: UserMaterial[];
      slideSets: SlideSet[];
      sharedMaterial: SharedMaterial[];
    };
  };
}[] {
  const flattenedNodesByModule: Record<string, NodeWithoutDescription[]> = {};

  moduleIds.forEach(moduleId => {
    flattenedNodesByModule[moduleId] = flattenNodes(nodesByModule[moduleId] || []);
  });

  return moduleIds.reduce<
    {
      moduleId: string;
      count: number;
      nodes: NodeWithoutDescription[];
      groupedByNode: {
        [nodeId: string]: {
          medialinks: Medialink[];
          userMaterial: UserMaterial[];
          slideSets: SlideSet[];
          sharedMaterial: SharedMaterial[];
        };
      };
    }[]
  >((acc, moduleId) => {
    const medialinksForModule = medialinks.filter(medialink => medialink.moduleId === moduleId);
    const medialinksByNode = groupMaterialsByNode(medialinksForModule);

    const userMaterialForModule = userMaterial.filter(x => x.moduleId === moduleId);
    const userMaterialByNode = groupUserMaterialByNode(userMaterialForModule);

    const slideSetsForModule = slideSets.filter(x => x.moduleId === moduleId);
    const slideSetsByNode = slideSetByNode(slideSetsForModule);

    const sharedMaterialsForModule = sharedMaterial.filter(x => x.moduleId === moduleId);
    const sharedMaterialsByNode = groupSharedMaterialByNode(sharedMaterialsForModule);

    const materialNodeIds = [
      ...Object.keys(sharedMaterialsByNode),
      ...Object.keys(slideSetsByNode),
      ...Object.keys(userMaterialByNode),
      ...Object.keys(medialinksByNode),
    ];

    const fallbackNodesById = getFallbackNodesById([
      ...medialinksForModule,
      ...userMaterialForModule,
      ...slideSetsForModule,
      ...sharedMaterialsForModule,
    ]);

    const resolvedNodes = [
      ...flattenedNodesByModule[moduleId],
      ...materialNodeIds
        .filter(nodeId => !flattenedNodesByModule[moduleId].some(node => node.id === nodeId))
        .map(
          nodeId =>
            fallbackNodesById[nodeId] || {
              id: nodeId,
              title: nodeId,
              displayPrefix: false,
              prefix: undefined,
            },
        ),
    ];

    const knownNodeIds = resolvedNodes.map(x => x.id);
    const orderedNodeIds = [
      ...knownNodeIds.filter(x => materialNodeIds.includes(x)),
      ...materialNodeIds.filter(x => !knownNodeIds.includes(x)),
    ];

    if (
      medialinksForModule.length > 0 ||
      userMaterialForModule.length > 0 ||
      slideSetsForModule.length > 0 ||
      sharedMaterialsForModule.length > 0
    ) {
      return [
        ...acc,
        {
          moduleId,
          count:
            medialinksForModule.length +
            userMaterialForModule.length +
            slideSetsForModule.length +
            sharedMaterialsForModule.length,
          nodes: resolvedNodes,
          groupedByNode: orderedNodeIds.reduce<
            Record<
              string,
              {
                medialinks: Medialink[];
                userMaterial: UserMaterial[];
                slideSets: SlideSet[];
                sharedMaterial: SharedMaterial[];
              }
            >
          >((prev, nodeId) => {
            return {
              ...prev,
              [nodeId]: {
                medialinks: medialinksByNode[nodeId] || [],
                userMaterial: userMaterialByNode[nodeId] || [],
                slideSets: slideSetsByNode[nodeId] || [],
                sharedMaterial: sharedMaterialsByNode[nodeId] || [],
              },
            };
          }, {}),
        },
      ];
    }

    return acc;
  }, []);
}
